import { config } from "../utils/settings";

class CameraManager {
    
    constructor(app) {
        this.app = app;
        this.cameraStream = null;
        this.cameraTexture = null;
        this.cameraSprite = null;
        this.videoElement = null;
        this.canvas = null;
    }
    
    async setupCamera() {
        try {
            const cameraInit = await this.initializeCamera();
            
            if (cameraInit && cameraInit.videoElement) {
                const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                
                this.videoElement = cameraInit.videoElement;

                // gonna ignore this OS specific thing for now, I'm pretty positive it's not necessary
                if (true) {
                    // iOS-specific approach using canvas as intermediate
                    this.canvas = document.createElement('canvas');
                    const context = this.canvas.getContext('2d');

                    this.setCamSizeIOS();
                    this.cameraTexture = PIXI.Texture.from(this.canvas);
                    this.cameraSprite = new PIXI.Sprite(this.cameraTexture);
                    
                    // Custom update function for iOS
                    this.updateCameraTexture = () => {
                        if (this.videoElement.readyState >= this.videoElement.HAVE_CURRENT_DATA) {
                            if (this.canvas.width !== window.innerWidth && window.innerWidth> 0) {
                                this.setCamSizeIOS();
                            }
                            
                            // Draw video frame to canvas
                            context.drawImage(this.videoElement, 0, 0, this.canvas.width, this.canvas.height);
                            this.cameraTexture.update();
                        }
                    };
                } else {
                    // Standard approach for other platforms
                    this.cameraTexture = PIXI.Texture.from(this.videoElement);
                    this.cameraSprite = new PIXI.Sprite(this.cameraTexture);
                    
                    // Standard update function
                    this.updateCameraTexture = () => {
                        // console.log("update standard");
                        if (this.cameraTexture) {
                            this.cameraTexture.update();
                        }
                    };
                }
                
                this.cameraSprite.alpha = 1.0; // Ensure visible
                this.cameraSprite.tint = 0xFFFFFF; // No tint
                this.cameraSprite.visible = true;
                
                this.resizeCameraSprite();

                // Mirror horizontally for selfie mode
                this.cameraSprite.scale.x = -1;
                
                // Add to stage at the very bottom (index 0)
                this.app.stage.addChildAt(this.cameraSprite, 0);
                
                // Start update loop
                this.app.ticker.add(this.updateCameraTexture);
                
                // console.log("Camera setup successful", {
                //     sprite: this.cameraSprite,
                //     texture: this.cameraTexture,
                //     stage: this.app.stage
                // });
                
                return true;
            } else {
                // Remove any existing sky background
                this.createSkyBackground(2000); // This will remove sky
            }
        } catch (error) {
            console.warn('Camera setup failed:', error);
            
            // Clean up any camera resources
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach(track => track.stop());
                this.cameraStream = null;
            }
        }
        
        return false;
    }

    setCamSizeIOS() {
        const [canvasWidth, canvasHeight] = this.recheckCorrectAspectRatio();
        
        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
    }
    
    resizeCameraSprite() {
        if (!this.cameraSprite) return;
    
        const [newWidth, newHeight] = this.recheckCorrectAspectRatio();
        
        this.cameraSprite.width = newWidth;
        this.cameraSprite.height = newHeight;

        this.cameraSprite.anchor.set(0.5);
        this.cameraSprite.position.set(config.width / 2, config.height / 2);
    }

    recheckCorrectAspectRatio() {
        const videoWidth = this.videoElement.videoWidth || config.width;
        const videoHeight = this.videoElement.videoHeight || config.height;
        const videoAspectRatio = videoWidth / videoHeight;
        
        let canvasWidth, canvasHeight;
        
        const containerAspectRatio = config.width / config.height;
        
        if (containerAspectRatio > videoAspectRatio) {
            canvasWidth = config.width;
            canvasHeight = canvasWidth / videoAspectRatio;
        } else {
            canvasHeight = config.height;
            canvasWidth = canvasHeight * videoAspectRatio;
        }

        return [canvasWidth, canvasHeight];
    }

    
    async initializeCamera() {
        try {
            // Check if mediaDevices is available
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                console.warn('Camera access not supported in this browser or context');
                return null;
            }
            
            // Check if we're in a secure context
            if (window.isSecureContext === false) {
                console.warn('Camera access requires a secure context (HTTPS)');
                return null;
            }
            
            // Request camera with front-facing (selfie) camera with error handling
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user" },
                width: { ideal: config.width },
                height: { ideal: config.height }
            }).catch(err => {
                console.warn('Camera access denied or not available:', err.message);
                return null;
            });
            
            if (!stream) return null;
            
            this.cameraStream = stream;
            
            // Create a video element for the camera feed
            const videoElement = document.createElement('video');
            videoElement.srcObject = stream;
            videoElement.autoplay = true;
            videoElement.playsinline = true; // Important for iOS
            videoElement.muted = true;
            
            videoElement.addEventListener('playing', () => {
                console.log('Camera video is playing!', {
                    width: videoElement.videoWidth,
                    height: videoElement.videoHeight,
                    readyState: videoElement.readyState
                });
            });
            
            // Wait for the video to be ready
            await new Promise(resolve => {
                videoElement.onloadedmetadata = () => {
                    videoElement.play().then(resolve);
                };
            });
            
            return {
                stream,
                videoElement
            };
        } catch (error) {
            console.error('Error accessing camera:', error);
            // Game can still run without camera
            return null;
        }
    }
    

    updateCameraTexture() {
        if (this.cameraTexture) {
            // This forces the texture to update with the latest frame from the video
            this.cameraTexture.update();
        }
    }

        
    // Alternative approach - add this method to GameManager and call it if camera init fails
    createSkyBackground(levelWidth) {
        if (!this.app) return;
        
        // Remove any existing sky background
        const existingSky = this.app.stage.children.find(
            child => child._skyBackground === true
        );
        
        if (existingSky) {
            this.app.stage.removeChild(existingSky);
        }
        
        // Only create sky background if camera isn't working
        if (!this.cameraSprite) {
            console.log("Creating sky background");
            // Create a sky as fallback
            const sky = new PIXI.Graphics();
            sky.beginFill(0x87CEEB); // Sky blue
            sky.drawRect(0, 0, Math.max(levelWidth, config.width * 2), config.height);
            sky.endFill();
            
            // Mark this as the sky background for later identification
            sky._skyBackground = true;
            
            // Add some clouds for visual interest
            for (let i = 0; i < Math.floor(levelWidth/200); i++) {
                const cloud = new PIXI.Graphics();
                const cloudX = Math.random() * levelWidth;
                const cloudY = Math.random() * (config.height / 2);
                const cloudSize = 30 + Math.random() * 50;
                
                cloud.beginFill(0xFFFFFF, 0.8);
                cloud.drawEllipse(0, 0, cloudSize, cloudSize / 2);
                cloud.drawEllipse(cloudSize / 2, -cloudSize / 4, cloudSize / 2, cloudSize / 3);
                cloud.endFill();
                
                cloud.x = cloudX;
                cloud.y = cloudY;
                
                sky.addChild(cloud);
            }
            
            // Add to stage at index 0 (background)
            this.app.stage.addChildAt(sky, 0);
        }
    }

    cleanup() {
        if (this.cameraStream) this.cameraStream.getTracks().forEach(track => track.stop());
        if (this.cameraTexture) this.cameraTexture.destroy(true);
    }
}

export default CameraManager;